import {
  DeesElement,
  customElement,
  html,
  css,
  cssManager,
  property,
  type TemplateResult,
} from '@design.estate/dees-element';
import { dashboardCardStyles } from './sz-dashboard-card.styles.js';

declare global {
  interface HTMLElementTagNameMap {
    'sz-certificates-card': SzCertificatesCard;
  }
}

@customElement('sz-certificates-card')
export class SzCertificatesCard extends DeesElement {
  public static demo = () => html`
    <div style="padding: 24px; display: flex; gap: 16px; flex-wrap: wrap;">
      <sz-certificates-card validCount="2"></sz-certificates-card>
      <sz-certificates-card validCount="5" expiringCount="2"></sz-certificates-card>
      <sz-certificates-card validCount="0" expiredCount="1"></sz-certificates-card>
    </div>
  `;

  public static demoGroups = ['Network'];

  @property({ type: Number })
  public accessor validCount: number = 0;

  @property({ type: Number })
  public accessor expiringCount: number = 0;

  @property({ type: Number })
  public accessor expiredCount: number = 0;

  public static styles = [
    cssManager.defaultStyles,
    dashboardCardStyles,
    css`
      :host {
        min-width: 200px;
      }

      .status-list {
        display: flex;
        flex-direction: column;
        gap: var(--dees-spacing-md);
      }

      .status {
        min-height: 28px;
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) auto;
        align-items: center;
        gap: var(--dees-spacing-sm);
      }

      .status.valid dees-icon {
        color: var(--dees-color-accent-success);
      }

      .status.warning dees-icon {
        color: var(--dees-color-accent-warning);
      }

      .status.error dees-icon {
        color: var(--dees-color-accent-error);
      }

      .status.empty dees-icon {
        color: var(--dees-color-text-muted);
      }

      .status-text {
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-callout-size);
      }
    `,
  ];

  public render(): TemplateResult {
    const hasCertificates = this.validCount > 0
      || this.expiringCount > 0
      || this.expiredCount > 0;

    return html`
      <dees-tile>
        <div slot="header" class="card-header">
          <div class="header-copy">
            <div class="card-title">Certificates</div>
            <div class="card-subtitle">TLS lifecycle status</div>
          </div>
          <dees-icon icon="lucide:ShieldCheck" .iconSize=${18}></dees-icon>
        </div>

        <div class="card-content">
          <div class="status-list">
            ${this.validCount > 0 ? html`
              <div class="status valid">
                <dees-icon icon="lucide:CircleCheck" .iconSize=${18}></dees-icon>
                <span class="status-text">Valid</span>
                <dees-badge
                  rounded
                  type="success"
                  .text=${String(this.validCount)}
                ></dees-badge>
              </div>
            ` : ''}

            ${this.expiringCount > 0 ? html`
              <div class="status warning">
                <dees-icon icon="lucide:ClockAlert" .iconSize=${18}></dees-icon>
                <span class="status-text">Expiring soon</span>
                <dees-badge
                  rounded
                  type="warning"
                  .text=${String(this.expiringCount)}
                ></dees-badge>
              </div>
            ` : ''}

            ${this.expiredCount > 0 ? html`
              <div class="status error">
                <dees-icon icon="lucide:CircleX" .iconSize=${18}></dees-icon>
                <span class="status-text">Expired</span>
                <dees-badge
                  rounded
                  type="error"
                  .text=${String(this.expiredCount)}
                ></dees-badge>
              </div>
            ` : ''}

            ${!hasCertificates ? html`
              <div class="status empty">
                <dees-icon icon="lucide:ShieldCheck" .iconSize=${18}></dees-icon>
                <span class="status-text">No certificates</span>
              </div>
            ` : ''}
          </div>
        </div>
      </dees-tile>
    `;
  }
}
